import {
  getPublishedInsights,
  getInsightBySlug,
  getPublishedStories,
  getStoryBySlug,
  getPublishedCareers,
  getCareerBySlug,
  getServiceCategories,
  getPublishedLeaders,
  getLeaderBySlug,
  getPageBySlug,
  getPublishedWhoWeServe,
  getPublishedTestimonials,
  getPublishedInsightTags,
} from "@/lib/api/public.functions";
import { articles, clientStories, serviceCategories } from "@/lib/content";
import { defaultTestimonials, defaultWhoWeServeItems } from "@/lib/marketing/defaults";
import { hasCraftContent } from "@/lib/craft/serialize";

export async function loadPageBySlug(slug: string) {
  try {
    const data = await getPageBySlug({ data: { slug } });
    if (data && hasCraftContent(data.craftState as string | object)) return data;
    if (data?.craftState) return data;
  } catch {
    /* fallback */
  }
  return null;
}

export async function loadInsightTags() {
  try {
    return await getPublishedInsightTags();
  } catch {
    return [];
  }
}

export async function loadFeaturedInsightTags() {
  const tags = await loadInsightTags();
  return tags.filter((tag) => tag.featured).map((tag) => tag.name);
}

export async function loadInsights() {
  try {
    const data = await getPublishedInsights();
    if (data?.length) return data;
  } catch {
    /* DB unavailable — fall back to static */
  }
  return articles;
}

export async function loadInsightBySlug(slug: string) {
  try {
    const data = await getInsightBySlug({ data: { slug } });
    if (data) return data;
  } catch {
    /* fallback */
  }
  return articles.find((a) => a.slug === slug) ?? null;
}

export async function loadStories() {
  try {
    const data = await getPublishedStories();
    if (data?.length) return data;
  } catch {
    /* fallback */
  }
  return clientStories;
}

export async function loadStoryBySlug(slug: string) {
  try {
    const data = await getStoryBySlug({ data: { slug } });
    if (data) return data;
  } catch {
    /* fallback */
  }
  return clientStories.find((s) => s.slug === slug) ?? null;
}

export async function loadCareers() {
  try {
    const data = await getPublishedCareers();
    if (data?.length) return data;
  } catch {
    /* fallback */
  }
  return [
    {
      id: "static-tax-associate",
      slug: "tax-associate",
      title: "Tax Associate",
      location: "Penang",
      employmentType: "Full-time",
      department: "Tax",
      applyEmail: "info@kslau.com",
      excerpt: "",
      contentHtml: "",
    },
  ];
}

export async function loadCareerBySlug(slug: string) {
  try {
    const data = await getCareerBySlug({ data: { slug } });
    if (data) return data;
  } catch {
    /* fallback */
  }
  const jobs = await loadCareers();
  return jobs.find((j) => j.slug === slug) ?? null;
}

export async function loadServiceCategories() {
  try {
    const data = await getServiceCategories();
    if (data?.length) return data;
  } catch {
    /* fallback */
  }
  return serviceCategories;
}

export async function loadLeaders() {
  try {
    const data = await getPublishedLeaders();
    if (data?.length) return data;
  } catch {
    /* fallback */
  }
  return [];
}

export async function loadWhoWeServe() {
  try {
    const data = await getPublishedWhoWeServe();
    if (data?.length) return data;
  } catch {
    /* DB unavailable — use defaults below */
  }
  return defaultWhoWeServeItems().map((item) => ({
    name: item.name,
    body: item.body,
    image: item.image,
  }));
}

export async function loadTestimonials() {
  try {
    const data = await getPublishedTestimonials();
    if (data?.length) return data;
  } catch {
    /* DB unavailable — use defaults below */
  }
  return defaultTestimonials.map((item) => ({ ...item }));
}

export async function loadLeaderBySlug(slug: string) {
  try {
    const data = await getLeaderBySlug({ data: { slug } });
    if (data) return data;
  } catch {
    /* fallback */
  }
  return null;
}
